<template>
  <div class="max-w-3xl mx-auto space-y-6">
    <!-- Success Header -->
    <div class="text-center space-y-4 py-6">
      
      <h1 class="text-3xl font-bold text-slate-900 dark:text-white">
        {{ $t('AI_ONBOARDING.ACTIVATION.TITLE') || '🎉 Xin chúc mừng!' }}
      </h1>
      
      <p class="text-lg text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.ACTIVATION.SUBTITLE') || 'Setup hoàn tất trong' }} 
        <span class="font-bold text-hilab dark:text-hilab-dark">{{ elapsedMinutes }}</span> 
        {{ $t('GENERAL.MINUTES') || 'phút' }}!
      </p>

      <div v-if="elapsedMinutes <= 7" class="inline-flex items-center gap-2 px-4 py-2 bg-hilab/10 dark:bg-hilab-dark/20 border border-hilab/30 dark:border-hilab-dark/40 rounded-full">
        <lucide-icon name="zap" :size="16" class="text-hilab dark:text-hilab-dark" />
        <p class="text-sm font-medium text-hilab dark:text-hilab-dark">
          {{ $t('AI_ONBOARDING.ACTIVATION.FAST_COMPLETION') || 'Bạn hoàn thành nhanh hơn 80% người dùng khác!' }}
        </p>
      </div>
    </div>

    <!-- Stats Summary -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div class="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-center">
        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ stats.faq_count }}</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.ACTIVATION.STAT_FAQS') || 'FAQs' }}
        </p>
      </div>
      <div class="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-center">
        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ stats.data_item_count }}</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.ACTIVATION.STAT_PRODUCTS') || 'Sản phẩm' }}
        </p>
      </div>
      <div class="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-center">
        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ stats.rule_count }}</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.ACTIVATION.STAT_RULES') || 'Quy tắc' }}
        </p>
      </div>
      <div class="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-center">
        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{ stats.sample_sentence_count }}</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.ACTIVATION.STAT_SAMPLES') || 'Mẫu câu' }}
        </p>
      </div>
    </div>

    <!-- Main CTA: Activate Bot -->
    <div class="p-6 bg-white dark:bg-slate-800 rounded-2xl border-2 border-hilab/30 dark:border-hilab-dark/40 shadow-lg">
      <div class="text-center space-y-4">
        
        <div>
          <h2 class="text-xl font-bold text-slate-900 dark:text-white mb-2">
            {{ $t('AI_ONBOARDING.ACTIVATION.READY_TITLE') || 'Bot đã sẵn sàng!' }}
          </h2>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('AI_ONBOARDING.ACTIVATION.READY_TEXT') || 'Kích hoạt ngay để bot bắt đầu trả lời khách hàng 24/7' }}
          </p>
        </div>
        
        <woot-button
          size="large"
          color-scheme="primary"
          class="!bg-hilab hover:!bg-hilab-dark dark:!bg-hilab-dark dark:hover:!bg-hilab !text-white px-8 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all"
          :loading="isActivating"
          @click="activateBot"
        >
          <lucide-icon name="power" :size="20" class="mr-2" />
          {{ $t('AI_ONBOARDING.ACTIVATION.ACTIVATE_BTN') || 'KÍCH HOẠT BOT NGAY!' }}
        </woot-button>
      </div>
    </div>

    <!-- Next Steps (Optional) -->
    <div class="p-5 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
      <h3 class="text-sm font-semibold text-slate-900 dark:text-white mb-3">
        {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_STEPS_TITLE') || 'Bổ sung thêm (sau cũng được):' }}
      </h3>
      
      <div class="space-y-2">
        <div class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors cursor-pointer border border-slate-200 dark:border-slate-700" @click="goToFAQs">
          <div class="w-9 h-9 rounded-lg bg-hilab/10 dark:bg-hilab-dark/20 flex items-center justify-center shrink-0">
            <lucide-icon name="list-plus" :size="18" class="text-hilab dark:text-hilab-dark" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-slate-900 dark:text-white">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_ADD_FAQS') || 'Thêm 10+ FAQs' }}
            </p>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_ADD_FAQS_DESC') || 'Càng nhiều FAQs, bot càng thông minh' }}
            </p>
          </div>
          <lucide-icon name="chevron-right" :size="16" class="text-slate-400 shrink-0" />
        </div>

        <div class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg opacity-50 cursor-not-allowed border border-slate-200 dark:border-slate-700">
          <div class="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
            <lucide-icon name="file-up" :size="18" class="text-slate-400" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-slate-900 dark:text-white">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_UPLOAD_FILES') || 'Upload files kiến thức' }}
            </p>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_UPLOAD_FILES_DESC') || 'PDF, Word, Excel... (🚧 Sắp ra mắt)' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors cursor-pointer border border-slate-200 dark:border-slate-700" @click="goToInboxes">
          <div class="w-9 h-9 rounded-lg bg-hilab/10 dark:bg-hilab-dark/20 flex items-center justify-center shrink-0">
            <lucide-icon name="plug-zap" :size="18" class="text-hilab dark:text-hilab-dark" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-slate-900 dark:text-white">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_INTEGRATE') || 'Tích hợp với website/fanpage' }}
            </p>
            <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              {{ $t('AI_ONBOARDING.ACTIVATION.NEXT_INTEGRATE_DESC') || 'Để khách hàng chat ngay trên trang của bạn' }}
            </p>
          </div>
          <lucide-icon name="chevron-right" :size="16" class="text-slate-400 shrink-0" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

export default {
  name: 'AiActivationStep',
  
  components: {
    LucideIcon,
  },

  emits: ['complete', 'skip'],

  data() {
    return {
      isActivating: false,
      isActivated: false,
    };
  },

  computed: {
    ...mapState('aiOnboarding', ['generated']),
    ...mapGetters('aiOnboarding', ['elapsedMinutes']),
    
    stats() {
      return this.generated?.stats || {
        faq_count: 0,
        data_item_count: 0,
        rule_count: 0,
        sample_sentence_count: 0,
      };
    },
  },

  methods: {
    ...mapActions('aiOnboarding', ['applyConfiguration', 'resetOnboarding']),

    getCanProceed() {
      return true; // Activation step luôn cho phép proceed
    },

    async activateBot() {
      if (this.isActivating) return;
      
      this.isActivating = true;
      
      try {
        await this.applyConfiguration();
        
        // Show success notification
        this.$nextTick(() => {
          window.bus.$emit('newToastMessage', {
            message: this.$t('AI_ONBOARDING.ACTIVATION.SUCCESS') || '🎉 Bot đã được kích hoạt thành công!',
            type: 'success',
          });
        });
        
        this.isActivated = true;
        
        // Mark onboarding as completed in localStorage
        const accountId = this.$route.params.accountId;
        localStorage.setItem(`ai_onboarding_completed_${accountId}`, Date.now());
        
      } catch (error) {
        console.error('Failed to activate bot:', error);
        window.bus.$emit('newToastMessage', {
          message: error.response?.data?.message || this.$t('AI_ONBOARDING.ACTIVATION.ERROR') || 'Không thể kích hoạt bot. Vui lòng thử lại.',
          type: 'error',
        });
      } finally {
        this.isActivating = false;
      }
    },

    skipForNow() {
      this.$emit('skip');
      this.goToDashboard();
    },

    goToDashboard() {
      const accountId = this.$route.params.accountId;
      this.$router.push(`/app/accounts/${accountId}/dashboard`);
      this.resetOnboarding();
      this.$emit('complete');
    },

    goToFAQs() {
      const accountId = this.$route.params.accountId;
      this.$router.push(`/app/accounts/${accountId}/settings/ai-settings?tab=business_data`);
      this.$emit('complete');
    },

    goToInboxes() {
      const accountId = this.$route.params.accountId;
      this.$router.push(`/app/accounts/${accountId}/settings/inboxes`);
      this.$emit('complete');
    },
  },
};
</script>
